import { Suspense, useCallback, useState } from 'react';
import { Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';

import { BreakModeGuard } from '@/components/BreakModeGuard';
import { EditProfileDialog } from '@/components/editProfile';
import { InactivityWarningDialog } from '@/components/InactivityWarningDialog';
import { LanguageDialog } from '@/components/LanguageDialog';
import { NotificationToastDialog } from '@/components/notification';
import { ShuttleModeGuard } from '@/components/ShuttleModeGuard';
import { Sidebar } from '@/components/Sidebar';
import { StartupRedirectGuard } from '@/components/StartupRedirectGuard';
import { Header, ToastProvider } from '@/components/ui';
import { Authentticatevalues, ROUTES } from '@/constants';
import { useBootstrapConfigQuery } from '@/hooks/auth';
import { useSessionManager } from '@/hooks/useSessionManager';
import { useSignalRNotifications } from '@/hooks/useSignalRNotifications';
// import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import {
  DashboardPage,
  ErrorPage,
  LoadingPage,
  LoginPage,
  OTPPage,
  SignupPage,
  MaintenancePage,
  PlantSelectionPage,
  // Feature pages
  CreateAuditTaskPage,
  CreateMoveTaskPage,
  AuditSummaryPage,
  NotificationsPage,
  DockDoorsPage,
  TrailersPage,
  YMSCapacityDashboardPage,
  DisplacementSummaryPage,
  FeedbackPage,
  AppGuidePage,
  LogoutSummaryPage,
  WorkModeSelectionPage,
  ShuttleTransferPage,
  BreakModePage,
  RestrictedAreaPage,
} from '@/pages';
import { UpdateUserLogAsync } from '@/service/AuthenticationService';
import {
  useAppSelector,
  selectPlant,
  selectUser,
  selectAccessToken,
  selectSessionId,
} from '@/store';

import { useTokenRefresh } from './hooks/useTokenRefresh';

import type { EditProfileFields } from '@/components/editProfile/EditProfileDialog';

// Protected route wrapper - requires authentication
function ProtectedRoute() {
  const isAuthenticatedUser = useAppSelector(selectUser);
  const accessToken = useAppSelector(selectAccessToken);

  if (!isAuthenticatedUser && !accessToken) {
    return <Navigate to={ROUTES.login} replace />;
  }

  return <Outlet />;
}

// Route guard that requires plant selection
// Redirects to plant selection if no plant is selected
function RequirePlantRoute() {
  const plant = useAppSelector(selectPlant);

  // If no plant selected, redirect to plant selection
  if (!plant) {
    return <Navigate to={ROUTES.plantSelection} replace />;
  }

  return <Outlet />;
}

// Public route wrapper (redirects to dashboard if logged in)
function PublicRoute() {
  const isAuthenticatedUser = useAppSelector(selectUser);

  if (isAuthenticatedUser) {
    return <Navigate to={ROUTES.dashboard} replace />;
  }

  return <Outlet />;
}

// Main layout with sidebar and header
function MainLayout() {
  const { showWarning, countdown, onContinue } = useSessionManager();
  useTokenRefresh();
  useSignalRNotifications();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const plant = useAppSelector(selectPlant);
  const sessionId = useAppSelector(selectSessionId);

  // Local sidebar state
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Edit profile dialog state
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editProfileFields, setEditProfileFields] = useState<EditProfileFields | null>(null);

  // Language dialog state
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);

  const handleEditProfile = useCallback(() => {
    if (!user) return;
    // Close sidebar
    setSidebarExpanded(false);
    setMobileSidebarOpen(false);
    setEditProfileFields({
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email ?? '',
      carrier: user.carrier?.length ? user.carrier.map((c) => c._id) : [],
      plants: user.plants?.length ? user.plants.map((p) => p._id) : [],
    });
    setEditProfileOpen(true);
  }, [user]);

  const handleLanguageClick = useCallback(() => {
    setSidebarExpanded(false);
    setMobileSidebarOpen(false);
    setLanguageDialogOpen(true);
  }, []);

  const handleMenuClick = useCallback(() => {
    if (window.innerWidth >= 768) {
      setSidebarExpanded((prev) => !prev);
    } else {
      setMobileSidebarOpen((prev) => !prev);
    }
  }, []);

  const handleShuttleModeClick = useCallback(() => {
    void UpdateUserLogAsync({
      user: user?._id ?? '',
      status: Authentticatevalues.UserStatusEndYardWork,
      activity_date_time: new Date().toISOString(),
      session_id: sessionId ?? '',
      plant_id: plant?._id,
    }).catch(() => undefined);
    navigate(ROUTES.shuttleTransfer);
  }, [navigate, user?._id, sessionId, plant?._id]);

  const handleBreakModeClick = useCallback(() => {
    void UpdateUserLogAsync({
      user: user?._id ?? '',
      status: Authentticatevalues.UserStatusEndYardWork,
      activity_date_time: new Date().toISOString(),
      session_id: sessionId ?? '',
      plant_id: plant?._id,
    }).catch(() => undefined);
    navigate(ROUTES.breakMode);
  }, [navigate, user?._id, sessionId, plant?._id]);

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <Sidebar
        expanded={sidebarExpanded}
        mobileOpen={mobileSidebarOpen}
        onToggle={() => {
          setSidebarExpanded((prev) => !prev);
        }}
        onMobileClose={() => {
          setMobileSidebarOpen(false);
        }}
        onEditProfile={handleEditProfile}
        onChangePlant={() => {
          navigate(ROUTES.changePlant);
        }}
        onLanguageClick={handleLanguageClick}
      />

      {/* Main content area - add left margin when sidebar is open on desktop */}
      <div className={`flex-1 flex flex-col min-w-0 ${sidebarExpanded ? 'md:ml-64' : ''}`}>
        <Header
          onMenuClick={handleMenuClick}
          onShuttleModeClick={handleShuttleModeClick}
          onBreakModeClick={handleBreakModeClick}
          sidebarOpen={sidebarExpanded}
        />
        <main className="flex-1 overflow-auto relative">
          <Outlet />
        </main>
      </div>

      {/* Edit Profile Dialog */}
      <EditProfileDialog
        isOpen={editProfileOpen}
        initialFields={editProfileFields}
        onClose={() => {
          setEditProfileOpen(false);
        }}
      />

      {/* Language Selection Dialog */}
      <LanguageDialog
        isOpen={languageDialogOpen}
        onClose={() => {
          setLanguageDialogOpen(false);
        }}
      />

      {/* SignalR push notification toast — matches Xamarin NotificationDialog */}
      <NotificationToastDialog />

      {/* Inactivity auto-logout warning — matches Xamarin MainActivity.cs */}
      <InactivityWarningDialog open={showWarning} countdown={countdown} onContinue={onContinue} />
    </div>
  );
}

export function App() {
  // Auth session is restored synchronously in userSlice initialState (from localStorage).
  // Global config (plants, settings, functionCodes) is also restored from localStorage.
  // This re-fetches config in the background to keep it fresh.
  useBootstrapConfigQuery();

  return (
    <ToastProvider>
      <BreakModeGuard />
      <ShuttleModeGuard />
      <StartupRedirectGuard />
      <Suspense fallback={<LoadingPage />}>
        <Routes>
          {/* Public routes */}
          <Route element={<PublicRoute />}>
            <Route path={ROUTES.login} element={<LoginPage />} />
            <Route path={ROUTES.otp} element={<OTPPage />} />
            <Route path={ROUTES.signup} element={<SignupPage />} />
          </Route>

          {/* Protected routes without header/sidebar */}
          <Route element={<ProtectedRoute />}>
            <Route path={ROUTES.plantSelection} element={<PlantSelectionPage />} />
            <Route path={ROUTES.workModeSelection} element={<WorkModeSelectionPage />} />
            <Route path={ROUTES.shuttleTransfer} element={<ShuttleTransferPage />} />
            <Route path={ROUTES.breakMode} element={<BreakModePage />} />
          </Route>

          {/* Protected routes with main layout (header + sidebar) */}
          <Route element={<ProtectedRoute />}>
            <Route element={<MainLayout />}>
              {/* Plant selection from sidebar - does not require existing plant */}
              <Route path={ROUTES.changePlant} element={<PlantSelectionPage />} />

              {/* Routes that require plant selection */}
              <Route element={<RequirePlantRoute />}>
                <Route path={ROUTES.dashboard} element={<DashboardPage />} />
                <Route path={ROUTES.createAuditTask} element={<CreateAuditTaskPage />} />
                <Route path={ROUTES.createMoveTask} element={<CreateMoveTaskPage />} />
                <Route path={ROUTES.auditSummary} element={<AuditSummaryPage />} />
                <Route path={ROUTES.notifications} element={<NotificationsPage />} />

                <Route path={ROUTES.dockDoors} element={<DockDoorsPage />} />
                <Route path={ROUTES.trailers} element={<TrailersPage />} />
                <Route path={ROUTES.ymsCapacityDashboard} element={<YMSCapacityDashboardPage />} />
                <Route path={ROUTES.displacementSummary} element={<DisplacementSummaryPage />} />
                <Route path={ROUTES.feedback} element={<FeedbackPage />} />
                <Route path={ROUTES.appGuide} element={<AppGuidePage />} />
                <Route path={ROUTES.logoutSummary} element={<LogoutSummaryPage />} />
              </Route>
            </Route>
          </Route>

          {/* Utility routes */}
          <Route path={ROUTES.restrictedArea} element={<RestrictedAreaPage />} />
          <Route path={ROUTES.maintenance} element={<MaintenancePage />} />
          <Route path={ROUTES.error} element={<ErrorPage />} />

          {/* Default redirect */}
          <Route path={ROUTES.home} element={<Navigate to={ROUTES.dashboard} replace />} />
          <Route path="*" element={<Navigate to={ROUTES.dashboard} replace />} />
        </Routes>
      </Suspense>
    </ToastProvider>
  );
}

export default App;
